import { spacing } from "../../app/design-system";
import { useFormDirty } from "../../../../../../shared/frontend/forms/useFormDirty";
/**
 * Wiederverwendbare Komponente „Ruhezeit eintragen“.
 * Kann aus dem Personenbereich, Aufgaben oder anderen Kontexten genutzt werden.
 */
import { Alert, App, Button, Form, Input, Select, Space, Typography } from "../../../../../../shared/frontend/ui";
import { DatePicker } from "../../../../../../shared/frontend/components/ControlledPickers";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import React from "react";
import { useAuth } from "../../app/auth/useAuth";
import { CONTRACT_STATUS_DRAFT } from "../../app/constants/contractStatus";
import { CYCLE_UNIT_MONTHLY } from "../../app/constants/cycleUnit";
import { Permissions } from "../../app/constants/permissions";
import { REST_PERIOD_BILLING_SCHEDULE_KEEP_DUE_DATES, REST_PERIOD_BILLING_SCHEDULE_RECOVER_TIME, REST_PERIOD_BILLING_SCHEDULE_SHIFT_DUE_DATES } from "../../app/constants/restPeriodBillingScheduleMode";
import { useI18n } from "../../app/i18n/useI18n";
import { useDateInputFormats } from "../../app/units/DateInputFormatContext";
import { decimalSeparatorOf,formatMoney,moneyPlaceholderOf } from "../../app/units/format";
import { loadUnits } from "../../app/units/unitsStorage";
import { DatePickerWithDuration } from "../../../../../../shared/frontend/components/DatePickerWithDuration";
import {
  ConfirmationDeliverySection,
  deliveryChannelToSendFlags,
  loadStoredDeliveryChannel,
  type DeliveryChannel
} from "../communication/DeliveryChannelSection";
import type { SendDocumentReviewRequest } from "../communication/SendDocumentReviewWorkspace";
import { FormDrawer } from "../FormDrawer";
import { FormSectionHeading } from "../FormSectionHeading";
import { ResponsiveFormGrid } from "../ui/antd";
import { RestPeriodImpactPreviewCard,type RestPeriodImpactPreview } from "./RestPeriodImpactPreviewCard";
import { RestPeriodFormFields } from "./RestPeriodFormFields";
import {
  latestAllowedRestPeriodDate,
  preferredRestPeriodContractId,
  sortRestPeriodContracts,
} from "./restPeriodContractChoice";
import { restPeriodFeeAmountUnit,restPeriodFeeCycleUnitForAmountUnit } from "./restPeriodFeeUtils";

import type { RestPeriodRow } from "./restPeriodRows";
import { useFeedback } from "../../../../../../shared/frontend/feedback/useFeedback";
import { ISO_DATE_LENGTH } from "../../app/utils/isoDate";
import { REST_FEE_MODE, REST_PERIOD_TEMPLATE_CUSTOM } from "./restPeriodFormModes";

const DAYS_PER_WEEK = 7;

export type { RestPeriodRow } from "./restPeriodRows";

type RestPeriodUpsertResponse = {
  restPeriod?: { id?: string };
  warnings?: string[] | null;
};

function resolveRestPeriodFeeMode(value: { monthlyFee?: number | null; weeksCount?: number | null }): string {
  if (value.monthlyFee != null && Number(value.monthlyFee) > 0) return REST_FEE_MODE.Amount;
  if (value.weeksCount != null && Number(value.weeksCount) > 0) return REST_FEE_MODE.Contributions;
  return REST_FEE_MODE.None;
}

function parseRestPeriodSaveResponse(body: unknown): { restPeriodId: string | null; warnings: string[] } {
  if (body && typeof body === "object" && "restPeriod" in (body as RestPeriodUpsertResponse)) {
    const r = body as RestPeriodUpsertResponse;
    return {
      restPeriodId: typeof r.restPeriod?.id === "string" ? r.restPeriod.id : null,
      warnings: (r.warnings || []).filter((x): x is string => typeof x === "string" && x.length > 0),
    };
  }
  return { restPeriodId: null, warnings: [] };
}

type RestPeriodTemplateLite = {
  id: string;
  name: string;
  monthlyFee?: number | null;
  feeAmountUnit?: number | null;
  feeCycleUnit?: number | null;
  feeCycleValue?: number | null;
  weeksCount?: number | null;
  proportionalCalculationMethod?: number | null;
  extendsContract: boolean;
  continuePayment: boolean;
  appendFreeToEnd: boolean;
  billingScheduleMode: number;
  allowCheckInDuringRestPeriod: boolean;
  defaultLetterTemplateId?: string | null;
  isActive: boolean;
};

type ContractLite = {
  id: string;
  planName: string;
  status: number;
  startDate: string;
  endDate?: string | null;
  primaryContractEndDate?: string | null;
  effectiveMembershipEndDate?: string | null;
  restPeriodTemplateId?: string | null;
  allowRestPeriods?: boolean;
  studioId?: string | null;
  termDefinitionId?: string | null;
  termValue?: number | null;
};

export type RestPeriodDrawerProps = {
  open: boolean;
  onClose: () => void;
  personId: string;
  /** Optionaler Kontext-Header (z. B. aus Aufgaben), damit ersichtlich ist, für wen/was die Ruhezeit erfasst wird. */
  context?: { personName?: string | null; contractPackageName?: string | null } | null;
  /** Bearbeiten: bestehende Ruhezeit; dann wird PUT statt POST verwendet. */
  editRestPeriod?: RestPeriodRow | null;
  /** Vorbelegung (z. B. aus Aufgaben/App). */
  prefill?: {
    contractPackageId?: string;
    startDate?: string;
    endDate?: string;
    note?: string | null;
    restPeriodReasonId?: string | null;
    draft?: RestPeriodRequestDraft | null;
  } | null;
  inboundEmail?: { id: string; updatedAt: string } | null;
  requestContext?: React.ReactNode;
  /** Wenn aus Aufgaben/App: Member-App-Anfrage-ID (wird beim Speichern mitgeschickt). */
  memberAppRequestId?: string | null;
  /** Nach erfolgreichem Speichern. */
  onSuccess?: () => void;
  /** Öffnet die Versandbearbeitung als Arbeitsfläche im Seiteninhalt der aufrufenden Seite. */
  onOpenDocumentReview: (request: SendDocumentReviewRequest) => void;
  /** Person-Email für Versandoptionen; optional. */
  personEmail?: string | null;
  /** Studios (für Versandoptionen); optional, wird aus Vertrag abgeleitet. */
  studios?: Array<{ id: string }>;
  /** Verträge (für Studio-Ableitung); optional. */
  contracts?: Array<{ id: string; studioId?: string | null }>;
};

type FormValues = {
  contractPackageId?: string | null;
  restPeriodTemplateId?: string | null;
  restPeriodReasonId?: string | null;
  restPeriodFeeMode?: string | null;
  monthlyFee?: number | null;
  feeAmountUnit?: number | null;
  feeCycleUnit?: number | string | null;
  feeCycleValue?: number | null;
  weeksCount?: number | null;
  proportionalCalculationMethod?: number | null;
  extendsContract?: boolean;
  continuePayment?: boolean;
  appendFreeToEnd?: boolean;
  billingScheduleMode?: number;
  allowCheckInDuringRestPeriod?: boolean;
  startDate?: Dayjs | null;
  endDate?: Dayjs | null;
  durationDays?: number | null;
  durationWeeks?: number | null;
  durationMonths?: number | null;
  note?: string | null;
  sendConfirmation?: boolean;
  /** Textvariante des Schreibens; leer = Grundfall. */
  variantId?: string | null;
  deliveryChannel?: DeliveryChannel;
  /** Ob das Schreiben vor dem Versand angesehen und im Wortlaut geaendert wird. */
  editBeforeSend?: boolean;
};

export type RestPeriodRequestDraft = Partial<Pick<FormValues,
  "restPeriodTemplateId" | "restPeriodFeeMode" | "monthlyFee" | "feeAmountUnit" | "feeCycleUnit" |
  "feeCycleValue" | "weeksCount" | "proportionalCalculationMethod" | "extendsContract" |
  "continuePayment" | "appendFreeToEnd" | "billingScheduleMode" | "allowCheckInDuringRestPeriod">>;

/** Neuer Einzelkanal-Key; die alten Mehrkanal-Keys (…DeliveryChannels) verfallen bewusst. */
const DELIVERY_CHANNEL_STORAGE_KEY = "m1member.restPeriodDeliveryChannel";

function hasPositiveDuration(values: Pick<FormValues, "durationDays" | "durationWeeks" | "durationMonths">): boolean {
  return (
    (values.durationDays != null && values.durationDays > 0) ||
    (values.durationWeeks != null && values.durationWeeks > 0) ||
    (values.durationMonths != null && values.durationMonths > 0)
  );
}

function calculateInclusiveDurationEndDate(
  startDate: Dayjs,
  values: Pick<FormValues, "durationDays" | "durationWeeks" | "durationMonths">
): Dayjs | null {
  let endDate = startDate;
  if (values.durationDays != null && values.durationDays > 0) {
    endDate = endDate.add(values.durationDays, "day");
  } else if (values.durationWeeks != null && values.durationWeeks > 0) {
    endDate = endDate.add(values.durationWeeks * DAYS_PER_WEEK, "day");
  } else if (values.durationMonths != null && values.durationMonths > 0) {
    endDate = endDate.add(values.durationMonths, "month");
  } else {
    return null;
  }

  return endDate.add(-1, "day");
}

function resolveRestPeriodDateRange(values: Pick<FormValues, "startDate" | "endDate" | "durationDays" | "durationWeeks" | "durationMonths">): { startIso: string; endIso: string } | null {
  const startIso = values.startDate?.isValid() ? values.startDate.format("YYYY-MM-DD") : null;
  if (!startIso) return null;

  if (
    (values.durationDays != null && values.durationDays > 0) ||
    (values.durationWeeks != null && values.durationWeeks > 0) ||
    (values.durationMonths != null && values.durationMonths > 0)
  ) {
    const startDate = dayjs(startIso);
    if (!startDate.isValid()) return null;
    const endDate = calculateInclusiveDurationEndDate(startDate, values);
    if (!endDate?.isValid()) return null;
    return { startIso, endIso: endDate.format("YYYY-MM-DD") };
  }

  const endIso = values.endDate?.isValid() ? values.endDate.format("YYYY-MM-DD") : null;
  if (!endIso) return null;
  return { startIso, endIso };
}

function buildRestPeriodBaseDto(vals: FormValues, personId: string, includeNote = true) {
  const contractPackageId = String(vals.contractPackageId || "").trim();
  if (!contractPackageId) return null;
  const range = resolveRestPeriodDateRange(vals);
  if (!range || range.startIso >= range.endIso) return null;
  const feeMode = vals.restPeriodFeeMode || resolveRestPeriodFeeMode(vals);
  const hasRestPeriodFee = feeMode === REST_FEE_MODE.Amount && vals.monthlyFee != null && Number(vals.monthlyFee) > 0;
  const hasContributionCount = feeMode === REST_FEE_MODE.Contributions && vals.weeksCount != null && Number(vals.weeksCount) > 0;

  return {
    contractPackageId,
    personId,
    restPeriodTemplateId: vals.restPeriodTemplateId && vals.restPeriodTemplateId !== REST_PERIOD_TEMPLATE_CUSTOM ? String(vals.restPeriodTemplateId) : null,
    restPeriodReasonId: vals.restPeriodReasonId ? String(vals.restPeriodReasonId) : null,
    monthlyFee: hasRestPeriodFee ? Number(vals.monthlyFee) : null,
    feeAmountUnit: hasRestPeriodFee ? (vals.feeAmountUnit != null ? Number(vals.feeAmountUnit) : CYCLE_UNIT_MONTHLY) : null,
    feeCycleUnit: hasRestPeriodFee ? (vals.feeCycleUnit ?? restPeriodFeeCycleUnitForAmountUnit(vals.feeAmountUnit)) : null,
    feeCycleValue: hasRestPeriodFee ? (vals.feeCycleValue ?? null) : null,
    weeksCount: hasContributionCount ? Number(vals.weeksCount) : null,
    proportionalCalculationMethod: vals.proportionalCalculationMethod ?? null,
    extendsContract: vals.extendsContract !== false,
    continuePayment: !!vals.continuePayment,
    appendFreeToEnd: !!vals.appendFreeToEnd,
    billingScheduleMode: vals.billingScheduleMode != null ? Number(vals.billingScheduleMode) : REST_PERIOD_BILLING_SCHEDULE_RECOVER_TIME,
    allowCheckInDuringRestPeriod: !!vals.allowCheckInDuringRestPeriod,
    startDate: range.startIso,
    endDate: range.endIso,
    note: includeNote ? ((vals.note || "").trim() || null) : null,
  };
}

function previewSignatureValue(value: unknown): unknown {
  if (dayjs.isDayjs(value)) return value.isValid() ? value.format("YYYY-MM-DD") : null;
  return value ?? null;
}

export function RestPeriodDrawer({
  open,
  onClose,
  personId,
  context,
  editRestPeriod,
  prefill,
  memberAppRequestId,
  inboundEmail,
  requestContext,
  onSuccess,
  onOpenDocumentReview,
  personEmail,
  studios = [],
  contracts = [],
}: RestPeriodDrawerProps) {
  const auth = useAuth();
  const canCustomizeRest = auth.hasAnyPermission(Permissions.RestPeriodsCustomize);
  const { text } = useI18n();
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const units = React.useMemo(() => loadUnits(auth.info?.accountId), [auth.info?.accountId]);
  const dateInputFormats = useDateInputFormats();
  const decimalSeparator = React.useMemo(() => decimalSeparatorOf(units), [units]);
  const moneyPlaceholder = React.useMemo(() => moneyPlaceholderOf(units), [units]);
  const currencyCode = (units.currency || "").trim() || "EUR";
  const fallbackStudioId = studios[0]?.id || null;

  const [form] = Form.useForm<FormValues>();
  const { dirty, initialize, resetDirty } = useFormDirty(form);
  const [saving, setSaving] = React.useState(false);
  const [templates, setTemplates] = React.useState<RestPeriodTemplateLite[]>([]);
  const [restPeriodReasons, setRestPeriodReasons] = React.useState<Array<{ id: string; name: string }>>([]);
  const [contractsList, setContractsList] = React.useState<ContractLite[]>([]);
  const [selectedContractId, setSelectedContractId] = React.useState<string | null>(null);
  const [isCustom, setIsCustom] = React.useState(false);
  const [lookupLoadFailed, setLookupLoadFailed] = React.useState(false);
  // Vier Abrufe beim Öffnen füllen Vorlagen-, Grund-, Vertrags- und Versandauswahl. Die
  // Auswirkungs-Vorschau hat eine eigene Anzeige; diese hier fehlte und ließ die Felder leer.
  const [lookupsLoading, setLookupsLoading] = React.useState(false);
  const [impactPreview, setImpactPreview] = React.useState<RestPeriodImpactPreview | null>(null);
  const [impactPreviewLoading, setImpactPreviewLoading] = React.useState(false);
  const [impactPreviewError, setImpactPreviewError] = React.useState<string | null>(null);
  const [restPeriodVariants, setRestPeriodVariants] = React.useState<Array<{ id: string; name: string }>>([]);
  const [letterSendingIntegrations, setLetterSendingIntegrations] = React.useState<
    Array<{ id: string; integrationType: string; displayName: string }>
  >([]);
  const watchedContractPackageId = Form.useWatch("contractPackageId", form);
  const watchedTemplateId = Form.useWatch("restPeriodTemplateId", form);
  // Gewähltes Modell: seine Werte als ein Satz, die Einzelwerte unter „Details“ (gesperrt wie bisher).
  const modelLocked = !!watchedTemplateId && watchedTemplateId !== REST_PERIOD_TEMPLATE_CUSTOM;
  const [modelDetailsOpen, setModelDetailsOpen] = React.useState(false);
  const summaryValues = Form.useWatch([], form) as Partial<FormValues> & Record<string, unknown> | undefined;
  const modelSummary = React.useMemo(() => {
    if (!modelLocked || !summaryValues) return "";
    const fee = Number(summaryValues.monthlyFee ?? 0) > 0
      ? text("Rest period summary fee").replace("{0}", formatMoney(Number(summaryValues.monthlyFee), units))
      : Number(summaryValues.weeksCount ?? 0) > 0
        ? text("Rest period summary fee").replace("{0}", text("Rest period summary contributions").replace("{0}", String(summaryValues.weeksCount)))
        : text("Rest period fee mode none");
    const billing = summaryValues.appendFreeToEnd
      ? text("Append to end")
      : Number(summaryValues.billingScheduleMode) === REST_PERIOD_BILLING_SCHEDULE_SHIFT_DUE_DATES
        ? text("Shift contribution due dates")
        : Number(summaryValues.billingScheduleMode) === REST_PERIOD_BILLING_SCHEDULE_KEEP_DUE_DATES
          ? text("Correct contributions")
          : text("Recover rest period time");
    const access = summaryValues.allowCheckInDuringRestPeriod ? text("Check-in allowed") : text("No check-in during rest period");
    return [fee, billing, access].join(" · ");
  }, [modelLocked, summaryValues, text, units]);
  const watchedStartDate = Form.useWatch("startDate", form);
  const watchedEndDate = Form.useWatch("endDate", form);
  const watchedDurationDays = Form.useWatch("durationDays", form);
  const watchedDurationWeeks = Form.useWatch("durationWeeks", form);
  const watchedDurationMonths = Form.useWatch("durationMonths", form);
  const watchedMonthlyFee = Form.useWatch("monthlyFee", form);
  const watchedFeeAmountUnit = Form.useWatch("feeAmountUnit", form);
  const watchedWeeksCount = Form.useWatch("weeksCount", form);
  const watchedExtendsContract = Form.useWatch("extendsContract", form);
  const watchedContinuePayment = Form.useWatch("continuePayment", form);
  const watchedAppendFreeToEnd = Form.useWatch("appendFreeToEnd", form);
  const watchedBillingScheduleMode = Form.useWatch("billingScheduleMode", form);
  const watchedNote = Form.useWatch("note", form);
  const watchedReasonId = Form.useWatch("restPeriodReasonId", form);
  const watchedCheckIn = Form.useWatch("allowCheckInDuringRestPeriod", form);
  const watchedSendConfirmation = Form.useWatch("sendConfirmation", form);
  const watchedEditBeforeSend = Form.useWatch("editBeforeSend", form);
  const plannedPreviewActions = watchedSendConfirmation
    ? [watchedEditBeforeSend ? "Rest period preview review confirmation" : "Rest period preview send confirmation"]
    : [];
  // Auch Änderungen an Grund, Notiz und Zutritt gehören in den vollständigen Nachweis.
  const restPeriodPreviewSignature = React.useMemo(
    () => JSON.stringify([
      previewSignatureValue(watchedContractPackageId),
      previewSignatureValue(watchedTemplateId),
      previewSignatureValue(watchedStartDate),
      previewSignatureValue(watchedEndDate),
      previewSignatureValue(watchedDurationDays),
      previewSignatureValue(watchedDurationWeeks),
      previewSignatureValue(watchedDurationMonths),
      previewSignatureValue(watchedMonthlyFee),
      previewSignatureValue(watchedFeeAmountUnit),
      previewSignatureValue(watchedWeeksCount),
      previewSignatureValue(watchedExtendsContract),
      previewSignatureValue(watchedContinuePayment),
      previewSignatureValue(watchedAppendFreeToEnd),
      previewSignatureValue(watchedBillingScheduleMode),
      previewSignatureValue(watchedNote),
      previewSignatureValue(watchedReasonId),
      previewSignatureValue(watchedCheckIn),
    ]),
    [
      watchedAppendFreeToEnd,
      watchedBillingScheduleMode,
      watchedNote,
      watchedReasonId,
      watchedCheckIn,
      watchedContinuePayment,
      watchedContractPackageId,
      watchedDurationDays,
      watchedDurationMonths,
      watchedDurationWeeks,
      watchedEndDate,
      watchedExtendsContract,
      watchedFeeAmountUnit,
      watchedMonthlyFee,
      watchedStartDate,
      watchedTemplateId,
      watchedWeeksCount
    ]
  );
  const applyRestPeriodTemplateFromList = React.useCallback(
    (templateList: RestPeriodTemplateLite[], templateId: string) => {
      const t = templateList.find((x) => String(x.id) === String(templateId));
      if (!t) return;
      const tplHasFee = t.monthlyFee != null && Number(t.monthlyFee) > 0;
      form.setFieldsValue({
        restPeriodTemplateId: String(t.id),
        restPeriodFeeMode: resolveRestPeriodFeeMode(t),
        monthlyFee: t.monthlyFee ?? null,
        feeAmountUnit: tplHasFee ? restPeriodFeeAmountUnit(t) : undefined,
        feeCycleUnit: tplHasFee ? (t.feeCycleUnit ?? restPeriodFeeCycleUnitForAmountUnit(restPeriodFeeAmountUnit(t))) : undefined,
        feeCycleValue: null,
        weeksCount: t.weeksCount ?? null,
        proportionalCalculationMethod: t.proportionalCalculationMethod ?? null,
        extendsContract: t.extendsContract !== false,
        continuePayment: !!t.continuePayment,
        appendFreeToEnd: !!t.appendFreeToEnd,
        billingScheduleMode: t.billingScheduleMode ?? REST_PERIOD_BILLING_SCHEDULE_RECOVER_TIME,
        allowCheckInDuringRestPeriod: !!t.allowCheckInDuringRestPeriod,
      });
    },
    [form]
  );

  /**
   * Die Varianten des Ruhezeitschreibens — Bestätigung, Ablehnung, vorzeitiges Ende. Ohne Auswahl
   * gilt der Grundfall.
   */
  const loadRestPeriodVariants = React.useCallback(async (studioId: string | null | undefined) => {
    const query = new URLSearchParams({ language: "de" });
    if (studioId) query.set("studioId", studioId);
    const variants = await auth.api.get<Array<{ id: string; name: string }>>(
      `/api/v1/system-documents/rest-period-confirmation/variants?${query.toString()}`
    );
    setRestPeriodVariants(variants || []);
    const current = form.getFieldValue("variantId");
    if (current && !(variants || []).some((variant) => variant.id === current)) {
      form.setFieldValue("variantId", null);
    }
  }, [auth.api, form]);

  // Daten laden
  React.useEffect(() => {
    if (!open || !personId) return;
    let alive = true;
    (async () => {
      await Promise.resolve();
      if (!alive) return;
      setTemplates([]);
      setRestPeriodReasons([]);
      setContractsList([]);
      setRestPeriodVariants([]);
      setLetterSendingIntegrations([]);
      setLookupLoadFailed(false);
      setLookupsLoading(true);
      const [tpl, reasons, ctr, integrations, appRequest] = await Promise.all([
        auth.api.get<RestPeriodTemplateLite[]>("/api/v1/rest-period-templates"),
        auth.api.get<Array<{ id: string; name: string }>>("/api/v1/rest-period-reasons"),
        auth.api.get<ContractLite[]>(`/api/v1/contract-packages?personId=${encodeURIComponent(personId)}`),
        auth.api.get<Array<{ id: string; integrationType: string; displayName: string }>>("/api/v1/integrations/by-capability/LetterSending"),
        memberAppRequestId ? auth.api.get<{ note?: string | null }>(`/api/v1/studio-app-requests/${encodeURIComponent(memberAppRequestId)}`) : Promise.resolve(null),
      ]);
      if (!alive) return;
      setTemplates(tpl || []);
      setRestPeriodReasons(reasons || []);
      setLetterSendingIntegrations(integrations || []);
      // Ohne Freigabe dürfen reguläre Benutzer keine neue Ruhezeit zuweisen. Bestehende
      // Ruhezeiten bleiben bearbeitbar; Benutzer mit Anpassungsrecht dürfen bewusst abweichen.
      const eligiblePackages = (ctr || []).filter((c) =>
        Number(c.status) !== CONTRACT_STATUS_DRAFT
        && (canCustomizeRest || c.allowRestPeriods !== false || c.id === editRestPeriod?.contractPackageId));
      const visiblePackages = sortRestPeriodContracts(eligiblePackages);
      setContractsList(visiblePackages);

      if (editRestPeriod) {
        setSelectedContractId(editRestPeriod.contractPackageId);
        setIsCustom(!editRestPeriod.restPeriodTemplateId);
        const selectedPackage = eligiblePackages.find((c) => c.id === editRestPeriod.contractPackageId);
        await loadRestPeriodVariants(selectedPackage?.studioId || fallbackStudioId);
        if (!alive) return;
        const hasFee = editRestPeriod.monthlyFee != null && Number(editRestPeriod.monthlyFee) > 0;
        const storedChannel = loadStoredDeliveryChannel(DELIVERY_CHANNEL_STORAGE_KEY, auth.info?.accountId);
        const hasEmail = !!String(personEmail || "").trim();
        const defaultChannel: DeliveryChannel =
          storedChannel && (storedChannel !== "email" || hasEmail) ? storedChannel : hasEmail ? "email" : "print";
        initialize({
          contractPackageId: editRestPeriod.contractPackageId,
          restPeriodTemplateId: editRestPeriod.restPeriodTemplateId ?? null,
          restPeriodReasonId: editRestPeriod.restPeriodReasonId ?? null,
          restPeriodFeeMode: resolveRestPeriodFeeMode(editRestPeriod),
          monthlyFee: editRestPeriod.monthlyFee ?? null,
          feeAmountUnit: hasFee ? restPeriodFeeAmountUnit(editRestPeriod) : undefined,
          feeCycleUnit: hasFee ? (editRestPeriod.feeCycleUnit ?? restPeriodFeeCycleUnitForAmountUnit(restPeriodFeeAmountUnit(editRestPeriod))) : undefined,
          feeCycleValue: editRestPeriod.feeCycleValue ?? null,
          weeksCount: editRestPeriod.weeksCount ?? null,
          proportionalCalculationMethod: editRestPeriod.proportionalCalculationMethod ?? null,
          extendsContract: editRestPeriod.extendsContract !== false,
          continuePayment: !!editRestPeriod.continuePayment,
          appendFreeToEnd: !!editRestPeriod.appendFreeToEnd,
          billingScheduleMode: editRestPeriod.billingScheduleMode ?? REST_PERIOD_BILLING_SCHEDULE_RECOVER_TIME,
          allowCheckInDuringRestPeriod: !!editRestPeriod.allowCheckInDuringRestPeriod,
          startDate: editRestPeriod.startDate ? dayjs(editRestPeriod.startDate.slice(0, ISO_DATE_LENGTH)) : dayjs(),
          endDate: editRestPeriod.endDate ? dayjs(editRestPeriod.endDate.slice(0, ISO_DATE_LENGTH)) : dayjs().add(1, "month"),
          durationDays: null,
          durationWeeks: null,
          durationMonths: null,
          note: editRestPeriod.note ?? null,
          sendConfirmation: !editRestPeriod.confirmationLetterSentAt,
          deliveryChannel: defaultChannel,
        });

      } else if (visiblePackages.length > 0) {
        const firstId = preferredRestPeriodContractId(visiblePackages, prefill?.contractPackageId);
        const first = visiblePackages.find((c) => c.id === firstId) ?? visiblePackages[0];
        const tplId = first?.restPeriodTemplateId ? String(first.restPeriodTemplateId) : null;
        let templateValues: Partial<FormValues> = {
          restPeriodFeeMode: REST_FEE_MODE.None,
          monthlyFee: null,
          feeAmountUnit: undefined,
          feeCycleUnit: undefined,
          feeCycleValue: null,
          weeksCount: null,
          proportionalCalculationMethod: null,
          extendsContract: true,
          continuePayment: false,
          appendFreeToEnd: false,
          billingScheduleMode: REST_PERIOD_BILLING_SCHEDULE_RECOVER_TIME,
          allowCheckInDuringRestPeriod: false,
        };
        if (tplId && (tpl || []).length > 0) {
          const t = (tpl || []).find((x) => String(x.id) === tplId);
          if (t) {
            const tplHasFee = t.monthlyFee != null && Number(t.monthlyFee) > 0;
            templateValues = {
              restPeriodFeeMode: resolveRestPeriodFeeMode(t),
              monthlyFee: t.monthlyFee ?? null,
              feeAmountUnit: tplHasFee ? restPeriodFeeAmountUnit(t) : undefined,
              feeCycleUnit: tplHasFee ? (t.feeCycleUnit ?? restPeriodFeeCycleUnitForAmountUnit(restPeriodFeeAmountUnit(t))) : undefined,
              feeCycleValue: null,
              weeksCount: t.weeksCount ?? null,
              proportionalCalculationMethod: t.proportionalCalculationMethod ?? null,
              extendsContract: t.extendsContract !== false,
              continuePayment: !!t.continuePayment,
              appendFreeToEnd: !!t.appendFreeToEnd,
              billingScheduleMode: t.billingScheduleMode ?? REST_PERIOD_BILLING_SCHEDULE_RECOVER_TIME,
              allowCheckInDuringRestPeriod: !!t.allowCheckInDuringRestPeriod,
            };
          }
        }
        const startD = prefill?.startDate ? dayjs(prefill.startDate.slice(0, ISO_DATE_LENGTH)) : dayjs();
        const endD = prefill?.endDate ? dayjs(prefill.endDate.slice(0, ISO_DATE_LENGTH)) : startD;
        const latestForCreate = first ? latestAllowedRestPeriodDate(first) : null;
        const cappedStart = latestForCreate && startD.format("YYYY-MM-DD") > latestForCreate
          ? dayjs(latestForCreate)
          : startD;
        await loadRestPeriodVariants(first?.studioId || fallbackStudioId);
        if (!alive) return;
        const storedChannel = loadStoredDeliveryChannel(DELIVERY_CHANNEL_STORAGE_KEY, auth.info?.accountId);
        const hasEmail = !!String(personEmail || "").trim();
        const defaultChannel: DeliveryChannel =
          storedChannel && (storedChannel !== "email" || hasEmail) ? storedChannel : hasEmail ? "email" : "print";
        setSelectedContractId(firstId || null);
        setIsCustom(prefill?.draft != null && !prefill.draft.restPeriodTemplateId);
        initialize({
          contractPackageId: firstId ?? null,
          restPeriodTemplateId: tplId ?? null,
          restPeriodReasonId: prefill?.restPeriodReasonId ?? null,
          ...templateValues,
          ...(prefill?.draft ?? {}),
          startDate: cappedStart,
          endDate: endD,
          durationDays: null,
          durationWeeks: null,
          durationMonths: null,
          note: prefill?.note ?? appRequest?.note ?? null,
          sendConfirmation: true,
          deliveryChannel: defaultChannel,
        });
      }
    })().catch(() => {
      if (alive) setLookupLoadFailed(true);
      // Central API toast shows error; keep the drawer from showing stale lookup data.
    }).finally(() => {
      if (alive) setLookupsLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [open, personId, editRestPeriod, prefill, memberAppRequestId, auth.api, auth.info?.accountId, form, initialize, canCustomizeRest, applyRestPeriodTemplateFromList, loadRestPeriodVariants, personEmail, fallbackStudioId]);

  const handleClose = () => {
    form.resetFields();
    setSelectedContractId(null);
    setIsCustom(false);
    setRestPeriodVariants([]);
    setLetterSendingIntegrations([]);
    onClose();
  };

  const selectedContract = selectedContractId ? contractsList.find((c) => c.id === selectedContractId) : null;
  const contractForStudio = selectedContractId ? contracts.find((c) => c.id === selectedContractId) : null;
  const studioId = contractForStudio?.studioId || selectedContract?.studioId || fallbackStudioId;
  const latestRestPeriodDate = selectedContract ? latestAllowedRestPeriodDate(selectedContract) : null;
  const disableFutureRestPeriodDate = React.useCallback((current: Dayjs | null) => {
    if (!current || !latestRestPeriodDate) return false;
    return current.format("YYYY-MM-DD") > latestRestPeriodDate;
  }, [latestRestPeriodDate]);

  const keepEndDateAfterStartDate = React.useCallback(
    (nextStartDate: Dayjs | null) => {
      const start = nextStartDate ? dayjs(nextStartDate).startOf("day") : null;
      if (!start?.isValid()) return;
      if (hasPositiveDuration(form.getFieldsValue(["durationDays", "durationWeeks", "durationMonths"]))) return;

      const currentEndDate = form.getFieldValue("endDate");
      const end = currentEndDate ? dayjs(currentEndDate).startOf("day") : null;
      if (!end?.isValid() || !end.isAfter(start, "day")) {
        form.setFieldValue("endDate", start.add(1, "day"));
      }
    },
    [form]
  );

  React.useEffect(() => {
    let alive = true;
    void (async () => {
      await Promise.resolve();
      if (!alive) return;
      if (!open || lookupsLoading || lookupLoadFailed) {
        setImpactPreview(null);
        setImpactPreviewLoading(false);
        setImpactPreviewError(null);
        return;
      }

      const dto = buildRestPeriodBaseDto(form.getFieldsValue(true) as FormValues, personId, true);
      if (!dto) {
        setImpactPreview(null);
        setImpactPreviewLoading(false);
        setImpactPreviewError(null);
        return;
      }

      setImpactPreviewLoading(true);
      setImpactPreviewError(null);
      auth.api.post<RestPeriodImpactPreview>(editRestPeriod
        ? `/api/v1/rest-periods/preview?restPeriodId=${encodeURIComponent(editRestPeriod.id)}`
        : "/api/v1/rest-periods/preview", dto, { suppressGlobalErrorToast: true })
        .then((preview) => {
          if (!alive) return;
          setImpactPreview(preview);
        })
        .catch((error: unknown) => {
          if (!alive) return;
          const messageText = error instanceof Error && error.message ? error.message : "Rest period preview unavailable";
          setImpactPreview(null);
          setImpactPreviewError(messageText);
        })
        .finally(() => {
          if (alive) setImpactPreviewLoading(false);
        });

    })();
    return () => {
      alive = false;
    };
  }, [auth.api, editRestPeriod, form, lookupLoadFailed, lookupsLoading, open, personId, restPeriodPreviewSignature]);

  const handleSave = async (v?: Record<string, unknown>) => {
    const vals = (v ?? await form.validateFields().catch(() => null)) as Record<string, unknown> | null;
    if (!vals) return;
    const contractPackageId = String(vals.contractPackageId || "").trim();
    if (!contractPackageId) {
      feedback.error(text("Please select"));
      return;
    }

    const range = resolveRestPeriodDateRange(vals as FormValues);
    if (!range?.startIso) {
      feedback.error(text("Start date is required"));
      return;
    }

    if (!range.endIso) {
      feedback.error(text("End date is required (or specify duration)"));
      return;
    }

    if (range.startIso >= range.endIso) {
      feedback.error(text("End date must be after start date"));
      return;
    }

    const selectedForSave = contractsList.find((contract) => contract.id === contractPackageId);
    const latestForSave = selectedForSave ? latestAllowedRestPeriodDate(selectedForSave) : null;
    // Bequemlichkeit: dieselbe Laufzeitregel wie das Backend (RestPeriodOutsideContractPackage).
    if (latestForSave && range.startIso > latestForSave) {
      feedback.error(text("RestPeriodOutsideContractPackage"));
      return;
    }

    const sendConfirmation = form.getFieldValue("sendConfirmation") === true;
    const variantId = form.getFieldValue("variantId") || null;
    // Vor dem Versand bearbeiten heisst: erst die Ruhezeit speichern, dann das Schreiben ansehen.
    // Vorher gibt es den Vorgang noch nicht, und ohne ihn zeigte die Ansicht Musterwerte statt
    // der Angaben dieses Mitglieds.
    const editBeforeSend = sendConfirmation && form.getFieldValue("editBeforeSend") === true;

    const deliveryChannel = (form.getFieldValue("deliveryChannel") ?? null) as DeliveryChannel | null;
    if (sendConfirmation && !deliveryChannel) {
      feedback.warning(text("Please select a delivery channel."));
      return;
    }
    const flags = sendConfirmation
      ? deliveryChannelToSendFlags(deliveryChannel)
      : deliveryChannelToSendFlags(null);
    const sendEmail = flags.sendEmail;

    if (sendConfirmation && editRestPeriod?.confirmationLetterSentAt) {
      const confirmed = await new Promise<boolean>((resolve) => {
        modal.confirm({
          title: text("Rest period confirmation already sent"),
          content: text("Rest period resend confirmation question"),
          okText: text("Send again"),
          cancelText: text("Cancel"),
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
      if (!confirmed) return;
    }
    const baseDto = buildRestPeriodBaseDto(vals as FormValues, personId);
    if (!baseDto) return;
    const dto = {
      ...baseDto,
      sendConfirmation: sendConfirmation && !editBeforeSend,
      variantId,
      sendEmail,
      language: "de",
      memberAppRequestId: editRestPeriod?.id ? undefined : (memberAppRequestId || undefined),
      inboundEmailId: editRestPeriod?.id ? undefined : inboundEmail?.id,
      inboundEmailUpdatedAt: editRestPeriod?.id ? undefined : inboundEmail?.updatedAt,
    };

    setSaving(true);
    try {
      const raw =
        editRestPeriod?.id != null
          ? await auth.api.put<unknown>(`/api/v1/rest-periods/${encodeURIComponent(editRestPeriod.id)}`, dto)
          : await auth.api.post<unknown>("/api/v1/rest-periods", dto);
      const { restPeriodId, warnings } = parseRestPeriodSaveResponse(raw);
      resetDirty();
      feedback.success(text("Rest period saved."));
      if (warnings.length > 0) {
        const hasRisk = warnings.some((w) => w.toLowerCase().includes("warning"));
        const list = (
          <ul style={{ paddingLeft: spacing.navigationGap, marginBottom: 0 }}>
            {warnings.map((w) => (
              <li key={w}>{text(w)}</li>
            ))}
          </ul>
        );
        if (hasRisk) {
          modal.warning({ title: text("Rest period save follow-up title"), content: list });
        } else {
          modal.info({ title: text("Rest period save follow-up title"), content: list });
        }
      }
      // Die Ruhezeit steht; jetzt das Schreiben. Die aufrufende Seite zeigt es als Arbeitsfläche,
      // deren Abbrechen ausdrücklich meldet, dass nichts hinausging.
      if (editBeforeSend && restPeriodId) {
        handleClose();
        onOpenDocumentReview({
          kind: "rest-period-confirmation",
          referenceId: restPeriodId,
          variantId,
          sendEmail,
          title: text("Rest period confirmation"),
          onCancel: () => {
            modal.info({
              title: text("Confirmation not sent"),
              content: text("Rest period saved without confirmation hint"),
            });
            onSuccess?.();
          },
          onSent: (result) => {
            if (result.warnings?.length) {
              modal.warning({
                title: text("Rest period save follow-up title"),
                content: (
                  <ul style={{ paddingLeft: spacing.navigationGap, marginBottom: 0 }}>
                    {result.warnings.map((warning) => (
                      <li key={warning}>{text(warning)}</li>
                    ))}
                  </ul>
                ),
              });
            } else {
              feedback.success(text("Confirmation sent."));
            }
            onSuccess?.();
          },
        });
        return;
      }
      handleClose();
      onSuccess?.();
    } catch {
      // Central API toast
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormDrawer
      title={editRestPeriod ? "Edit rest period" : "Add rest period"}
      dirty={dirty}
      open={open}
      onCancel={handleClose}
      formId="rest-period-form"
      submitting={saving}
      contentLoading={lookupsLoading}
      hideSubmit={lookupLoadFailed}
    >
      {requestContext}
      {lookupLoadFailed ? (
        <Alert type="error" showIcon message={text("Error loading data")} style={{ marginBottom: spacing.lg }} />
      ) : null}
      {(context?.personName || context?.contractPackageName) && (
        <div style={{ marginBottom: spacing.md }}>
          <Space direction="vertical" size={2} style={{ width: "100%" }}>
            {context?.personName ? (
              <Typography.Text>
                <Typography.Text type="secondary">{text("Person")}: </Typography.Text>
                <Typography.Text strong>{String(context.personName)}</Typography.Text>
              </Typography.Text>
            ) : null}
            {context?.contractPackageName ? (
              <Typography.Text>
                <Typography.Text type="secondary">{text("Contract package")}: </Typography.Text>
                <Typography.Text strong>{String(context.contractPackageName)}</Typography.Text>
              </Typography.Text>
            ) : null}
          </Space>
        </div>
      )}
      {editRestPeriod?.confirmationLetterSentAt ? (
        <Alert type="info" showIcon style={{ marginBottom: spacing.md }} message={text("Rest period edit prior confirmation hint")} />
      ) : null}
      <Form
        id="rest-period-form"
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={handleSave}
      >
        <FormSectionHeading first title={text("Rest period")} sectionKey="rest-periods.details">
          <Form.Item
            name="contractPackageId"
            label={text("Contract package")}
            rules={[{ required: true, message: text("Please select") }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              disabled={!!editRestPeriod}
              placeholder={text("Select contract package")}
              options={contractsList.map((c) => ({ value: c.id, label: c.planName || c.id }))}
              onChange={(value) => {
                setSelectedContractId(value || null);
                const contract = contractsList.find((c) => c.id === value);
                if (!contract) return;
                const latest = latestAllowedRestPeriodDate(contract);
                if (latest) {
                  const start = form.getFieldValue("startDate");
                  if (start && dayjs(start).format("YYYY-MM-DD") > latest) {
                    form.setFieldValue("startDate", dayjs(latest));
                  }
                }
                form.setFieldValue("variantId", null);
                void loadRestPeriodVariants(contract.studioId || fallbackStudioId);
                const tid = contract.restPeriodTemplateId ? String(contract.restPeriodTemplateId) : null;
                if (!canCustomizeRest) {
                  setIsCustom(false);
                  form.setFieldValue("restPeriodTemplateId", tid);
                  if (tid) applyRestPeriodTemplateFromList(templates, tid);
                  return;
                }
                if (tid) {
                  form.setFieldValue("restPeriodTemplateId", tid);
                  setIsCustom(false);
                  applyRestPeriodTemplateFromList(templates, tid);
                }
              }}
            />
          </Form.Item>
          {!canCustomizeRest && selectedContract && !selectedContract.restPeriodTemplateId ? (
            <Typography.Paragraph type="warning" style={{ marginTop: -(spacing.md), marginBottom: spacing.sm }}>
              {text("Rest period contract package missing template warning")}
            </Typography.Paragraph>
          ) : null}
          <Form.Item name="restPeriodReasonId" label={text("Rest period reason")}>
            <Select
              options={restPeriodReasons.map((r) => ({ value: r.id, label: r.name }))}
              placeholder={text("Select rest period reason…")}
              allowClear
            />
          </Form.Item>

          <div style={{ marginBottom: spacing.xl }}>
          <ResponsiveFormGrid>
            <Form.Item name="startDate" label={text("Start")} rules={[{ required: true, message: text("Required") }]}>
              <DatePicker
                allowClear={false}
                format={dateInputFormats}
                style={{ width: "100%" }}
                disabledDate={disableFutureRestPeriodDate}
                onChange={(date) => {
                  form.setFieldValue("startDate", date);
                  keepEndDateAfterStartDate(date);
                }}
              />
            </Form.Item>
            <Form.Item
              name="endDate"
              label={text("End")}
              rules={[
                {
                  validator: (_, value) => {
                    const startDate = form.getFieldValue("startDate");
                    const dDays = form.getFieldValue("durationDays");
                    const dWeeks = form.getFieldValue("durationWeeks");
                    const dMonths = form.getFieldValue("durationMonths");
                    if ((dDays != null && dDays > 0) || (dWeeks != null && dWeeks > 0) || (dMonths != null && dMonths > 0)) {
                      return Promise.resolve();
                    }
                    if (!value) return Promise.reject(new Error(text("Required or specify duration")));
                    if (startDate && value && !dayjs(value).isAfter(dayjs(startDate), "day")) {
                      return Promise.reject(new Error(text("End date must be after start date")));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <DatePickerWithDuration
                value={watchedEndDate}
                onChange={(date) => form.setFieldValue("endDate", date)}
                text={text}
                startDate={watchedStartDate}
                durationDays={watchedDurationDays}
                onDurationDaysChange={(v) => form.setFieldValue("durationDays", v)}
                durationWeeks={watchedDurationWeeks}
                onDurationWeeksChange={(v) => form.setFieldValue("durationWeeks", v)}
                durationMonths={watchedDurationMonths}
                onDurationMonthsChange={(v) => form.setFieldValue("durationMonths", v)}
                inclusiveDurationEnd
                showDays
                showWeeks
                showMonths
                format={dateInputFormats}
                placeholder={text("Select end date")}
                allowClear={false}
              />
            </Form.Item>
          </ResponsiveFormGrid>
          </div>

          <Form.Item
            name="restPeriodTemplateId"
            label={text("Rest period template")}
            normalize={(value) => value === REST_PERIOD_TEMPLATE_CUSTOM ? null : value}
            getValueProps={(value) => ({ value: value || (canCustomizeRest ? "__custom__" : undefined) })}
            extra={canCustomizeRest && watchedTemplateId ? text("Rest period custom configuration hint") : undefined}
          >
            <Select
              allowClear={canCustomizeRest}
              showSearch
              optionFilterProp="label"
              disabled={!canCustomizeRest}
              placeholder={text("Select template or configure individually…")}
              options={[
                ...(
                  !canCustomizeRest && selectedContract?.restPeriodTemplateId
                    ? templates.filter((t) => String(t.id) === String(selectedContract.restPeriodTemplateId))
                    : templates
                ).map((t) => ({ value: t.id, label: t.isActive ? t.name : `${t.name} (${text("Inactive")})` })),
                ...((canCustomizeRest || isCustom) ? [{ value: "__custom__", label: text("Custom"), disabled: !canCustomizeRest }] : []),
              ]}
              onChange={(value) => {
                if (value && value !== REST_PERIOD_TEMPLATE_CUSTOM) {
                  setIsCustom(false);
                  const t = templates.find((x) => String(x.id) === value);
                  if (t) {
                    const tplHasFee = t.monthlyFee != null && Number(t.monthlyFee) > 0;
                    form.setFieldsValue({
                      restPeriodFeeMode: resolveRestPeriodFeeMode(t),
                      monthlyFee: t.monthlyFee ?? null,
                      feeAmountUnit: tplHasFee ? restPeriodFeeAmountUnit(t) : undefined,
                      feeCycleUnit: tplHasFee ? (t.feeCycleUnit ?? restPeriodFeeCycleUnitForAmountUnit(restPeriodFeeAmountUnit(t))) : undefined,
                      feeCycleValue: null,
                      weeksCount: t.weeksCount ?? null,
                      proportionalCalculationMethod: t.proportionalCalculationMethod ?? null,
                      extendsContract: t.extendsContract !== false,
                      continuePayment: !!t.continuePayment,
                      appendFreeToEnd: !!t.appendFreeToEnd,
                      billingScheduleMode: t.billingScheduleMode ?? REST_PERIOD_BILLING_SCHEDULE_RECOVER_TIME,
                      allowCheckInDuringRestPeriod: !!t.allowCheckInDuringRestPeriod,
                    });
                  }
                } else {
                  setIsCustom(true);
                  form.setFieldValue("restPeriodTemplateId", null);
                }
              }}
            />
          </Form.Item>
          {!canCustomizeRest && selectedContract?.restPeriodTemplateId ? (
            <Typography.Paragraph type="secondary" style={{ marginTop: -(spacing.sm), marginBottom: spacing.sm }}>
              {text("Rest period model locked to contract help")}
            </Typography.Paragraph>
          ) : null}
          <Form.Item name="note" label={text("Note")}>
            <Input.TextArea rows={2} />
          </Form.Item>
        </FormSectionHeading>

        {modelLocked ? (
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: spacing.sm, marginBottom: spacing.md }}>
            <Typography.Text type="secondary">{modelSummary}</Typography.Text>
            <Button type="link" size="small" style={{ paddingInline: 0 }} onClick={() => setModelDetailsOpen((open) => !open)}>
              {text(modelDetailsOpen ? "Fewer details" : "Details")}
            </Button>
          </div>
        ) : null}
        {/* Zugeklappt bleiben die Felder gerendert: Ihre Werte gehören zum Speichern. */}
        <div hidden={modelLocked && !modelDetailsOpen}>
          <RestPeriodFormFields
            form={form}
            mode="instance"
            currencyCode={currencyCode}
            decimalSeparator={decimalSeparator}
            moneyPlaceholder={moneyPlaceholder}
            units={units}
            first={false}
            modelFieldsDisabled={!canCustomizeRest || modelLocked}
          />
        </div>

        {studioId ? (
          <FormSectionHeading title={text("Confirmation")} sectionKey="rest-periods.confirmation">
            <ConfirmationDeliverySection
              open={open}
              form={form}
              studioId={studioId}
              personEmail={personEmail}
              integrations={letterSendingIntegrations}
              variantOptions={restPeriodVariants.map((variant) => ({ value: variant.id, label: variant.name }))}
              variantLabel={text("Rest period confirmation")}
              storageKey={DELIVERY_CHANNEL_STORAGE_KEY}
              showEditBeforeSend
            />
          </FormSectionHeading>
        ) : null}
        <RestPeriodImpactPreviewCard
          preview={impactPreview}
          loading={impactPreviewLoading}
          error={impactPreviewError}
          units={units}
          plannedActions={plannedPreviewActions}
        />
      </Form>
    </FormDrawer>
  );
}
